import type { ReactNode } from 'react';
import React from 'react';

import {
  FieldGroupLabel,
  type FieldGroupLabelType,
} from '@/app/components/forms/field-group/label';
import {
  FieldGroupMessage,
  type FieldGroupMessageType,
} from '@/app/components/forms/field-group/message';
import { type CustomFormStyleConfig } from '@/app/components/forms/styles';
import { cn } from '@/app/utils/cn';

export interface FieldGroupErrorType {
  /**
   * (Optional) Error state
   */
  error?: boolean;
  /**
   * (Optional) Error message when error state = true
   */
  errorMessage?: string;
}

interface FieldGroupDisabledType {
  /**
   * (Optional) Disabled state
   */
  disabled?: boolean;
}

export interface FieldGroupType
  extends FieldGroupDisabledType,
    FieldGroupErrorType,
    FieldGroupLabelType,
    FieldGroupMessageType {
  ref?: React.Ref<any>;
}

export type InputType = 'input' | 'control';

interface FieldGroupProps extends FieldGroupType {
  inputType?: InputType;
  children?: ReactNode;
  /**
   * To override form group styling by using tailwind css
   */
  className?: string;
  /**
   * Custom theme styling
   */
  customStyle?: CustomFormStyleConfig['fieldGroup'];
}

export const FieldGroup = ({
  className,
  label,
  secondaryLabel,
  htmlFor,
  errorMessage,
  message,
  inputType = 'input',
  children,
  length,
  maxLength,
  customStyle,
  required = false,
  error = false,
  showMaxLength = false,
  theme = 'light',
  disabled = false,
  ...rest
}: FieldGroupProps) => {
  const canDisplayMaxLength =
    length !== undefined &&
    maxLength !== undefined &&
    maxLength > 0 &&
    showMaxLength;

  const canDisplayMessage =
    message?.length || errorMessage?.length || canDisplayMaxLength;
  const hasLabelOrMessage = !!label || !!canDisplayMaxLength;

  const Component = inputType === 'control' ? 'fieldset' : 'div';

  return hasLabelOrMessage ? (
    <Component
      {...rest}
      className={cn(
        'space-y-2',
        {
          'pointer-events-none': disabled,
        },
        className
      )}
    >
      {!!label && (
        <FieldGroupLabel
          inputType={inputType}
          htmlFor={htmlFor}
          label={label}
          secondaryLabel={secondaryLabel}
          required={required}
          theme={theme}
          customStyle={customStyle}
        />
      )}
      {children}
      {!!canDisplayMessage && (
        <FieldGroupMessage
          message={error ? errorMessage : message}
          error={!!error}
          length={length}
          maxLength={maxLength}
          showMaxLength={canDisplayMaxLength}
          theme={theme}
          customStyle={customStyle}
        />
      )}
    </Component>
  ) : (
    children
  );
};
